// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  Checkbox,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { createStyleSheet } from "../utils/styles";
import { theme } from "../../theme";
import type {
  FurtherTuningDialogProps,
  FurtherTuningOption,
  TuningOptionValue,
} from "../types/projects";

const optionOrder: FurtherTuningOption[] = [
  { labelKey: "increaseAccuracy" },
  { labelKey: "reduceFalseNegatives" },
  { labelKey: "reduceFalsePositives" },
];

const FurtherTuningDialog = (props: FurtherTuningDialogProps) => {
  const { t } = useTranslation("FurtherTuningDialog");
  const { isOpen, onClose, projectName } = props;

  const [selectedOptions, setSelectedOptions] = useState<TuningOptionValue[]>([]);

  const hasAnySelected = selectedOptions.length > 0;
  const options = useMemo(
    () =>
      optionOrder.map(option => ({
        labelKey: option.labelKey,
        label: t(option.labelKey),
      })),
    [t]
  );
  const toggleOption = useCallback(
    (key: TuningOptionValue) =>
      setSelectedOptions(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      ),
    []
  );
  const handleClose = useCallback(() => {
    setSelectedOptions([]);
    onClose();
  }, [onClose]);
  const handleSubmit = useCallback(() => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL || t("email.supportEmail");
    const subject = t("email.subject", { projectName });

    // Map selected options to appropriate body content
    const bodyContentMap: Record<TuningOptionValue, string> = {
      increaseAccuracy: t("email.accuracyBody"),
      reduceFalseNegatives: t("email.negativeBody"),
      reduceFalsePositives: t("email.positiveBody"),
    };

    // Get the appropriate body content based on selected options
    let body = "";
    if (selectedOptions.length === 1) {
      // Single option selected - use specific body
      body = bodyContentMap[selectedOptions[0]];
    } else if (selectedOptions.length > 1) {
      // Multiple options selected - combine bodies
      const bodies = selectedOptions.map(option => bodyContentMap[option]);
      body = bodies.join("\n\n");
    }

    // Add footer to the email body
    body += "\n\n" + t("email.footer");

    // Clean up the body text to ensure proper formatting
    body = body.replace(/\n\n+/g, "\n\n").trim();

    // Create mailto link with proper encoding
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const anchorElement = document.createElement("a");
    anchorElement.href = mailtoLink;
    anchorElement.style.display = "none";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    handleClose();
  }, [handleClose, projectName, selectedOptions, t]);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs" slotProps={{ paper: { sx: styles.dialog } }}>
      <DialogTitle sx={styles.title}>{t("title")}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" sx={styles.description}>
          {t("description")}
        </Typography>
        <Box sx={styles.dialogContents}>
          {options.map(option => (
            <Box key={option.label}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={styles.optionRow}
              >
                <Box>
                  <Typography variant="body1bold" color="primary">
                    {option.label}
                  </Typography>
                </Box>
                <Checkbox
                  checked={selectedOptions.includes(option.labelKey as TuningOptionValue)}
                  onChange={() => toggleOption(option.labelKey as TuningOptionValue)}
                  slotProps={{ input: { "aria-label": option.labelKey } }}
                />
              </Stack>
              <Divider />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={handleClose} variant="text">
          {t("common:cancel")}
        </Button>
        <Button onClick={handleSubmit} variant="text" disabled={!hasAnySelected}>
          {t("sendRequest")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FurtherTuningDialog;

const styles = createStyleSheet({
  dialog: {
    background: theme.palette.background.paper,
  },
  title: {
    fontWeight: 700,
  },
  description: { color: theme.palette.grey[600] },
  dialogContents: { bgcolor: "background.paper", borderRadius: 1, mt: 2 },
  optionRow: {
    gap: 2,
    py: 1,
  },
  actions: {
    px: 3,
    py: 2,
    "& .MuiButtonBase-root": {
      textTransform: "none",
    },
  },
});
